// =============================================================================
// PEDRA — src/lib/csv.ts
// Serializacao e leitura de CSV.
//
// SAIDA: separador `;` (padrao do Excel em pt-BR), decimal com virgula,
// fim de linha CRLF e BOM UTF-8 no download (sem BOM o Excel come os acentos).
// ENTRADA: delega a `paste.ts`, que ja aceita `,` `;` TAB `|`, aspas do
// RFC 4180, cabecalho opcional em pt-BR ou ingles e virgula decimal.
// =============================================================================

import type { OptimizationResult, Part, Settings, StockSheet } from '../core/types';
import { formatArea, fromMicron, toExactText } from '../core/units';
import { parsePastedParts, parsePastedStock } from './paste';
import { cutAxisLabel, grainShortLabel, yesNo } from './format';

// -----------------------------------------------------------------------------
// CONSTANTES
// -----------------------------------------------------------------------------

/** Separador de coluna usado em toda a exportacao. */
export const CSV_DELIMITER = ';';

/** Fim de linha. CRLF porque e o que o Excel espera. */
export const CSV_EOL = '\r\n';

/** Marca de ordem de bytes UTF-8. Obrigatoria para o Excel ler acentos. */
export const CSV_BOM = '\uFEFF';

/** Cabecalho canonico das tabelas de peca e de chapa. */
export const CSV_HEADER = 'Comprimento;Largura;Qtd;Nome;Material;Veio;Ativa';

// -----------------------------------------------------------------------------
// HELPERS DE ESCRITA
// -----------------------------------------------------------------------------

/** Envolve em aspas quando o campo tem separador, aspas, quebra ou espaco nas pontas. */
export function escapeCsvCell(value: string): string {
  const s = value ?? '';
  if (s === '') return '';
  if (/[";\r\n\t]/.test(s) || s !== s.trim()) {
    return `"${s.replace(/"/g, '""')}"`;
  }
  return s;
}

/** Junta uma linha de celulas ja convertidas em texto. */
function row(cells: ReadonlyArray<string | number>): string {
  return cells.map((c) => escapeCsvCell(String(c))).join(CSV_DELIMITER);
}

/** Monta o arquivo inteiro a partir das linhas. */
function join(lines: readonly string[]): string {
  return lines.join(CSV_EOL) + CSV_EOL;
}

/** Medida em Micron -> texto pt-BR sem sufixo, respeitando unidade e casas. */
function num(v: number, settings: Settings): string {
  return fromMicron(v, settings.unit, settings.decimalPlaces);
}

/**
 * Medida para as tabelas REIMPORTAVEIS (peças e chapas).
 *
 * Aqui nao se pode usar as casas decimais de exibicao: em metros com 1 casa a
 * chapa de 2,75 m viraria "2,8" (50 mm a menos) e em polegadas toda medida
 * seria empurrada para o multiplo de 1/16" mais proximo (~1,3 mm). O CSV
 * exportado tem que voltar exatamente igual — as casas de exibicao viram
 * apenas o MINIMO de casas escritas.
 */
function exact(v: number, settings: Settings): string {
  return toExactText(v, settings.unit, settings.decimalPlaces);
}

// -----------------------------------------------------------------------------
// EXPORTACAO — PECAS E CHAPAS
// -----------------------------------------------------------------------------

/**
 * Lista de pecas em CSV. Reimportavel: o cabecalho gerado aqui e reconhecido
 * por `parsePartsCsv` e pela colagem na grade.
 */
export function partsToCsv(parts: readonly Part[], settings: Settings): string {
  const lines: string[] = [CSV_HEADER];
  for (const p of parts) {
    lines.push(
      row([
        exact(p.length, settings),
        exact(p.width, settings),
        p.qty,
        p.label,
        p.material,
        p.orientation === 0 ? '' : grainShortLabel(p.orientation),
        yesNo(p.enabled),
      ]),
    );
  }
  return join(lines);
}

/**
 * Lista de chapas em CSV. Quantidade vazia significa estoque ilimitado —
 * exatamente como na tabela da interface.
 */
export function stockToCsv(stock: readonly StockSheet[], settings: Settings): string {
  const lines: string[] = [CSV_HEADER];
  for (const s of stock) {
    lines.push(
      row([
        exact(s.length, settings),
        exact(s.width, settings),
        s.qty === null ? '' : s.qty,
        s.label,
        s.material,
        s.orientation === 0 ? '' : grainShortLabel(s.orientation),
        yesNo(s.enabled),
      ]),
    );
  }
  return join(lines);
}

// -----------------------------------------------------------------------------
// EXPORTACAO — RESULTADO
// -----------------------------------------------------------------------------

/** Descricao curta de um retangulo filho de corte. */
function childText(
  child: { width: number; length: number; isWaste: boolean },
  settings: Settings,
): string {
  const dims = `${num(child.length, settings)} × ${num(child.width, settings)}`;
  return child.isWaste ? `${dims} (sobra)` : dims;
}

/**
 * Sequencia de cortes de todas as chapas — o arquivo que vai para a maquina.
 * Uma linha por corte, na ordem em que o operador deve executar.
 */
export function cutsToCsv(result: OptimizationResult, settings: Settings): string {
  const lines: string[] = [
    'Chapa;Nº da chapa;Repetições;Material;Seq;Estágio;Eixo;Posição;Início;Fim;Comprimento;Peça-mãe;Resultado A;Resultado B',
  ];

  for (const sheet of result.sheets) {
    for (const cut of sheet.cuts) {
      lines.push(
        row([
          sheet.label,
          sheet.index + 1,
          sheet.repeat,
          sheet.material,
          cut.seq,
          cut.stage,
          cutAxisLabel(cut.axis),
          num(cut.pos, settings),
          num(cut.start, settings),
          num(cut.end, settings),
          num(cut.length, settings),
          `${num(cut.parent.length, settings)} × ${num(cut.parent.width, settings)}`,
          childText(cut.childA, settings),
          childText(cut.childB, settings),
        ]),
      );
    }
  }

  if (lines.length === 1) lines.push(row(['Nenhum corte foi gerado.']));
  return join(lines);
}

/**
 * Sobras de cada chapa, separando o que da para reaproveitar do que e perda.
 */
export function offcutsToCsv(result: OptimizationResult, settings: Settings): string {
  const lines: string[] = [
    'Chapa;Nº da chapa;Repetições;Material;X;Y;Comprimento;Largura;Área;Aproveitável',
  ];

  for (const sheet of result.sheets) {
    for (const off of sheet.offcuts) {
      lines.push(
        row([
          sheet.label,
          sheet.index + 1,
          sheet.repeat,
          sheet.material,
          num(off.x, settings),
          num(off.y, settings),
          num(off.length, settings),
          num(off.width, settings),
          formatArea(off.area, settings.unit),
          yesNo(off.usable),
        ]),
      );
    }
  }

  if (lines.length === 1) lines.push(row(['Nenhuma sobra foi gerada.']));
  return join(lines);
}

/**
 * Plano de corte peca a peca: onde cada peca fica em cada chapa.
 * Este e o arquivo que costuma ser levado para o chao de fabrica ou para o ERP.
 */
export function placementsToCsv(result: OptimizationResult, settings: Settings): string {
  const lines: string[] = [
    'Chapa;Nº da chapa;Repetições;Material;Peça;Nome;X;Y;Comprimento;Largura;Girada',
  ];

  let counter = 0;
  for (const sheet of result.sheets) {
    for (const pl of sheet.placements) {
      counter++;
      lines.push(
        row([
          sheet.label,
          sheet.index + 1,
          sheet.repeat,
          pl.material,
          counter,
          pl.label,
          num(pl.x, settings),
          num(pl.y, settings),
          num(pl.length, settings),
          num(pl.width, settings),
          yesNo(pl.rotated),
        ]),
      );
    }
  }

  if (result.unfit.length > 0) {
    lines.push('');
    lines.push('NÃO COUBERAM');
    lines.push('Nome;Comprimento;Largura;Qtd;Material;Motivo');
    for (const u of result.unfit) {
      lines.push(
        row([
          u.label,
          num(u.length, settings),
          num(u.width, settings),
          u.qty,
          u.material,
          u.message,
        ]),
      );
    }
  }

  return join(lines);
}

// -----------------------------------------------------------------------------
// IMPORTACAO
// -----------------------------------------------------------------------------

/**
 * Le um CSV/TXT de pecas. Detecta separador, aspas, cabecalho e virgula
 * decimal automaticamente. Nunca lanca.
 */
export function parsePartsCsv(text: string, settings: Settings): { parts: Part[]; errors: string[] } {
  const result = parsePastedParts(text, settings);
  return { parts: result.rows, errors: result.errors };
}

/** Le um CSV/TXT de chapas. Quantidade em branco = estoque ilimitado. */
export function parseStockCsv(
  text: string,
  settings: Settings,
): { stock: StockSheet[]; errors: string[] } {
  const result = parsePastedStock(text, settings);
  return { stock: result.rows, errors: result.errors };
}

// -----------------------------------------------------------------------------
// DOWNLOAD
// -----------------------------------------------------------------------------

/** true quando estamos num navegador com DOM e suporte a Blob URL. */
function canDownload(): boolean {
  return (
    typeof document !== 'undefined' &&
    typeof Blob !== 'undefined' &&
    typeof URL !== 'undefined' &&
    typeof URL.createObjectURL === 'function'
  );
}

/** Dispara o download de um Blob com o nome informado. */
export function downloadBlob(filename: string, blob: Blob): void {
  if (!canDownload()) return;
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  anchor.rel = 'noopener';
  anchor.style.display = 'none';
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  // Revogar na hora quebra o download no Safari; um respiro resolve.
  setTimeout(() => URL.revokeObjectURL(url), 2000);
}

/**
 * Baixa um texto como arquivo. Para CSV, acrescenta o BOM UTF-8 se ainda
 * nao houver — sem ele o Excel abre "Divisória" como "DivisÃ³ria".
 */
export function downloadText(
  filename: string,
  text: string,
  mime = 'text/csv;charset=utf-8',
): void {
  if (!canDownload()) return;
  const needsBom = mime.includes('csv') && !text.startsWith(CSV_BOM);
  const content = needsBom ? CSV_BOM + text : text;
  downloadBlob(filename, new Blob([content], { type: mime }));
}
